// Runtime-resolved color values for JS contexts (charts, canvas, SVG, component props).
// Imports from themes.module.scss — the single source of truth for all color primitives.

import colors from '@/styles/themes.module.scss';

const darkPrimitives = {
  // Brand
  brandMain: colors.brandMain,
  brandLight: colors.brandLight,
  brandMuted: colors.brandMuted,
  brandSecondary: colors.brandSecondary,

  // Bright Red
  redBright60: colors.redBright60,
  redBright20: colors.redBright20,

  // Neutral
  neutral100: colors.neutral100,
  neutral60: colors.neutral60,
  neutral50: colors.neutral50,
  neutral40: colors.neutral40,
  neutral30: colors.neutral30,
  neutral20: colors.neutral20,
  neutral10: colors.neutral10,
  neutral5: colors.neutral5,
  neutral0: colors.neutral0,

  // Purple
  purple60: colors.purple60,
  purple50: colors.purple50,
  purple40: colors.purple40,
  purple30: colors.purple30,
  purple20: colors.purple20,

  // Blue
  blue60: colors.blue60,
  blue50: colors.blue50,
  blue40: colors.blue40,
  blue30: colors.blue30,
  blue20: colors.blue20,

  // Teal
  teal60: colors.teal60,
  teal50: colors.teal50,
  teal40: colors.teal40,
  teal30: colors.teal30,
  teal20: colors.teal20,

  // Red
  red60: colors.red60,
  red50: colors.red50,
  red40: colors.red40,
  red30: colors.red30,
  red20: colors.red20,

  // Orange
  orange60: colors.orange60,
  orange50: colors.orange50,
  orange40: colors.orange40,
  orange30: colors.orange30,
  orange20: colors.orange20,

  // Green
  green60: colors.green60,
  green50: colors.green50,
  green40: colors.green40,
  green30: colors.green30,
  green20: colors.green20,
} as const;

const darkColors = {
  ...darkPrimitives,

  // Semantic (mirrors themes.module.scss var() references)
  background: darkPrimitives.neutral0,
  surface1: darkPrimitives.neutral5,
  surface2: darkPrimitives.neutral10,
  textDefault: darkPrimitives.neutral100,
  textMuted: darkPrimitives.neutral50,
  textLink: darkPrimitives.brandLight,
  border: darkPrimitives.neutral20,
  colorError: darkPrimitives.redBright60,
  colorErrorBackground: darkPrimitives.redBright20,
  colorErrorText: darkPrimitives.red50,
} as const satisfies Record<string, string>;

const lightPrimitives = {
  // Brand
  brandMain: colors.brandMainLight,
  brandLight: colors.brandLightLight,
  brandMuted: colors.brandMutedLight,
  brandSecondary: colors.brandSecondaryLight,

  // Bright Red
  redBright60: colors.redBright60Light,
  redBright20: colors.redBright20Light,

  // Neutral
  neutral100: colors.neutral100Light,
  neutral60: colors.neutral60Light,
  neutral50: colors.neutral50Light,
  neutral40: colors.neutral40Light,
  neutral30: colors.neutral30Light,
  neutral20: colors.neutral20Light,
  neutral10: colors.neutral10Light,
  neutral5: colors.neutral5Light,
  neutral0: colors.neutral0Light,

  // Purple
  purple60: colors.purple60Light,
  purple50: colors.purple50Light,
  purple40: colors.purple40Light,
  purple30: colors.purple30Light,
  purple20: colors.purple20Light,

  // Blue
  blue60: colors.blue60Light,
  blue50: colors.blue50Light,
  blue40: colors.blue40Light,
  blue30: colors.blue30Light,
  blue20: colors.blue20Light,

  // Teal
  teal60: colors.teal60Light,
  teal50: colors.teal50Light,
  teal40: colors.teal40Light,
  teal30: colors.teal30Light,
  teal20: colors.teal20Light,

  // Red
  red60: colors.red60Light,
  red50: colors.red50Light,
  red40: colors.red40Light,
  red30: colors.red30Light,
  red20: colors.red20Light,

  // Orange
  orange60: colors.orange60Light,
  orange50: colors.orange50Light,
  orange40: colors.orange40Light,
  orange30: colors.orange30Light,
  orange20: colors.orange20Light,

  // Green
  green60: colors.green60Light,
  green50: colors.green50Light,
  green40: colors.green40Light,
  green30: colors.green30Light,
  green20: colors.green20Light,
} as const;

const lightColors = {
  ...lightPrimitives,

  // Semantic (mirrors themes.module.scss var() references)
  background: lightPrimitives.neutral5,
  surface1: lightPrimitives.neutral0,
  surface2: lightPrimitives.neutral5,
  textDefault: lightPrimitives.neutral100,
  textMuted: lightPrimitives.neutral50,
  textLink: lightPrimitives.brandLight,
  border: lightPrimitives.neutral20,
  colorError: lightPrimitives.redBright60,
  colorErrorBackground: lightPrimitives.redBright20,
  colorErrorText: lightPrimitives.red50,
} as const satisfies Record<string, string>;

// Use structural type so dark and light are both assignable
export type ThemeColors = { readonly [K in keyof typeof darkColors]: string };

export function getThemeColors(): ThemeColors {
  const theme = document.documentElement.dataset.theme;
  return theme === 'light' ? lightColors : darkColors;
}
